import React, { useEffect, ReactNode, ReactElement, useRef, useState, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { ConfigProvider, App as AntdApp, Spin } from 'antd';
import { Provider, useSelector, useDispatch } from 'react-redux';
import { store } from './store/store';
import { getUserProfile } from './store/slices/authSlice';
import { fetchActivationInfo } from './store/slices/activationSlice';
import { RootState } from './store/store';
import { AppDispatch } from './store/store';
import axios from 'axios';
import { CurrencyProvider } from './contexts/CurrencyContext';
import { useTranslation } from 'react-i18next';
import { getAntdLocale } from './i18n';
import './i18n';

// Layout - 首屏必需，同步加载
import MainLayout from './components/Layout/MainLayout';

// 首屏必需组件 - 同步加载
import Login from './pages/Login';
import Forbidden from './pages/Forbidden';
import RoleProtectedRoute from './components/RoleProtectedRoute';
import PostLoginRedirector from './components/PostLoginRedirector';

// 懒加载页面 - 按需加载，大幅减少首屏体积
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Products = lazy(() => import('./pages/Products'));
const StockOverview = lazy(() => import('./pages/Stock/Overview'));
const LowStock = lazy(() => import('./pages/Stock/LowStock'));
const AdjustStock = lazy(() => import('./pages/Stock/AdjustStock'));
const OrderList = lazy(() => import('./pages/Purchase/OrderList'));
const CreateOrder = lazy(() => import('./pages/Purchase/CreateOrder'));
const CgOrdersPage = lazy(() => import('./pages/Procurement/CgOrders/index'));
const CgOrdersCreatePage = lazy(() => import('./pages/Procurement/CgOrders/CreatePage'));
const Settings = lazy(() => import('./pages/Settings'));
const Suppliers = lazy(() => import('./pages/Suppliers'));
const RemoteClients = lazy(() => import('./pages/RemoteClients'));
const YGOrders = lazy(() => import('./pages/YGOrders'));
const YGVendedor = lazy(() => import('./pages/YGVendedor'));
const TerminalsManagement = lazy(() => import('./pages/TerminalsManagement'));
const Users = lazy(() => import('./pages/Users'));
const Finance = lazy(() => import('./pages/Finance'));
const Expenses = lazy(() => import('./pages/Finance/Expenses'));
const ProfitReport = lazy(() => import('./pages/Finance/ProfitReport'));
const CashFlow = lazy(() => import('./pages/Finance/CashFlow'));
const Payables = lazy(() => import('./pages/Finance/Payables'));
const Receivables = lazy(() => import('./pages/Finance/Receivables'));
const CheckManagement = lazy(() => import('./pages/Finance/CheckManagement'));
const PayableCheckManagement = lazy(() => import('./pages/Finance/PayableCheckManagement'));
const CashierSettlement = lazy(() => import('./pages/Finance/CashierSettlement'));
const CustomerPrepayments = lazy(() => import('./pages/Finance/CustomerPrepayments'));
const CustomerStatements = lazy(() => import('./pages/Finance/CustomerStatements'));
const StockDifference = lazy(() => import('./pages/Finance/StockDifference'));
const OrderDetail = lazy(() => import('./pages/Purchase/OrderDetail'));
const CashierPage = lazy(() => import('./pages/Cashier/CashierPage'));
const CashierPage2 = lazy(() => import('./pages/Cashier/CashierPage2'));
const AuditLogs = lazy(() => import('./pages/AuditLogs'));
const StoresSales = lazy(() => import('./pages/StoresSales'));
const Customers = lazy(() => import('./pages/Customers'));
const SelectStore = lazy(() => import('./pages/SelectStore'));
const Welcome = lazy(() => import('./pages/Welcome'));
const PDAStocktaking = lazy(() => import('./pages/PDAStocktaking'));
const Stocktaking = lazy(() => import('./pages/Stocktaking'));
const ReceiptTemplateEditor = lazy(() => import('./pages/ReceiptTemplate'));
const NewStock = lazy(() => import('./pages/NewStock'));
const PrinterShare = lazy(() => import('./pages/PrinterShare'));
const BackupPage = lazy(() => import('./pages/Backup'));
const TransportList = lazy(() => import('./pages/Transport/TransportList'));
const TransportModeList = lazy(() => import('./pages/Transport/TransportModeList'));
const TransferList = lazy(() => import('./pages/StockTransfer/TransferList'));
const TransferCreate = lazy(() => import('./pages/StockTransfer/TransferCreate'));
const TransferDetail = lazy(() => import('./pages/StockTransfer/TransferDetail'));
const TransferScan = lazy(() => import('./pages/StockTransfer/TransferScan'));

// Member Management Pages
const Members = lazy(() => import('./pages/Members'));
const MembersNew = lazy(() => import('./pages/Members/MembersNew'));
const Promotions = lazy(() => import('./pages/Promotions'));
const PromotionCreatePage = lazy(() => import('./pages/Promotions/PromotionCreatePage'));
const Coupons = lazy(() => import('./pages/Coupons'));
const PointsSettings = lazy(() => import('./pages/PointsSettings'));
const ProductCategories = lazy(() => import('./pages/ProductCategories'));
const TaxCategories = lazy(() => import('./pages/TaxCategories'));
const WhatsAppManagement = lazy(() => import('./pages/WhatsAppManagement'));

// 页面加载中的占位组件
const PageLoading = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
    <Spin size="large" />
  </div>
);

// 收银页面包装组件
const CashierPageWithCurrency = () => {
  const { storeId } = useParams<{ storeId: string }>();
  return (
    <CurrencyProvider storeId={storeId ? parseInt(storeId) : undefined}>
      <CashierPage />
    </CurrencyProvider>
  );
};

const CashierPage2WithCurrency = () => {
  const { storeId } = useParams<{ storeId: string }>();
  return (
    <CurrencyProvider storeId={storeId ? parseInt(storeId) : undefined}>
      <CashierPage2 />
    </CurrencyProvider>
  );
};

// Dashboard页面包装组件
const DashboardWithCurrency = () => {
  const { storeId } = useParams<{ storeId: string }>();
  return (
    <CurrencyProvider storeId={storeId ? parseInt(storeId) : undefined}>
      <Dashboard />
    </CurrencyProvider>
  );
};

// Implement this route guard for protected routes
const ProtectedRoute = ({ children }: { children: ReactNode }): ReactElement => {
  const { isAuthenticated, isLoading } = useSelector((state: RootState) => state.auth);

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  return <>{children}</>;
};

// Initialize user authentication
const AppInitializer = () => {
  const dispatch = useDispatch<AppDispatch>();
  const appInitRef = useRef(false);
  const { i18n } = useTranslation();
  const { user } = useSelector((state: RootState) => state.auth);

  // 当用户登录后，应用用户的语言偏好
  useEffect(() => {
    if (user?.preferred_language) {
      i18n.changeLanguage(user.preferred_language);
    }
  }, [user, i18n]);

  useEffect(() => {
    if (appInitRef.current) return;
    appInitRef.current = true;

    // 🔒 安全修复：检查本地存储中的token并验证其有效性
    const token = localStorage.getItem('token');
    if (token) {
      // 派发获取用户资料的action，验证token是否有效
      // 如果token无效，getUserProfile会返回rejected，触发清除状态
      dispatch(getUserProfile());
      // 同时拉取激活信息（allowed_pages），用于前端菜单权限控制
      dispatch(fetchActivationInfo());
    }

    // 配置全局axios请求拦截器
    axios.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('token');
        if (token && config.headers) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // 🔒 安全修复：配置全局axios响应拦截器处理401错误
    axios.interceptors.response.use(
      (response) => response,
      (error) => {
        const shouldSkipAuthRedirect = Boolean(error?.config?.skipAuthRedirect);
        if (error.response && error.response.status === 401 && !shouldSkipAuthRedirect) {
          // 收到401响应，说明token无效或已过期
          // 清除本地存储的token和认证状态
          localStorage.removeItem('token');
          // 如果不是在登录页，跳转到登录页
          if (!window.location.pathname.includes('/login')) {
            window.location.href = '/login';
          }
        }
        return Promise.reject(error);
      }
    );

  }, [dispatch]);

  return null;
};

// App configuration
const AppContent = () => {
  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <AppInitializer />
      <PostLoginRedirector />
      <Suspense fallback={<PageLoading />}>
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<Login />} />
        <Route path="/403" element={<Forbidden />} />

        {/* 根路径：由 PostLoginRedirector 根据角色自动跳转，此处仅做身份验证 */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <div>正在跳转...</div>
              </div>
            </ProtectedRoute>
          }
        />

        {/* Store dashboard (store specific) */}
        <Route
          path="/stores/:storeId/dashboard"
          element={
            <RoleProtectedRoute roles={["super_admin", "store_owner", "admin"]}>
              <MainLayout>
                <DashboardWithCurrency />
              </MainLayout>
            </RoleProtectedRoute>
          }
        />

        {/* Select store page */}
        <Route
          path="/select-store"
          element={
            <RoleProtectedRoute roles={["super_admin", "store_owner", "cashier", "admin"]}>
              <MainLayout>
                <SelectStore />
              </MainLayout>
            </RoleProtectedRoute>
          }
        />

        {/* Welcome page (no stores) */}
        <Route
          path="/welcome"
          element={
            <RoleProtectedRoute roles={["super_admin", "store_owner", "cashier", "admin"]}>
              <MainLayout>
                <Welcome />
              </MainLayout>
            </RoleProtectedRoute>
          }
        />

        {/* Dashboard route for管理员*/}
        <Route
          path="/dashboard"
          element={
            <RoleProtectedRoute roles={["super_admin", "store_owner"]}>
              <MainLayout>
                <CurrencyProvider>
                  <Dashboard />
                </CurrencyProvider>
              </MainLayout>
            </RoleProtectedRoute>
          }
        />

        {/* Admin dashboard alias */}
        <Route
          path="/admin/dashboard"
          element={
            <RoleProtectedRoute roles={["super_admin"]}>
              <MainLayout>
                <CurrencyProvider>
                  <Dashboard />
                </CurrencyProvider>
              </MainLayout>
            </RoleProtectedRoute>
          }
        />

        {/* 财务（多店销售）路由 */}
        <Route path="/finance" element={<Navigate to="/finance/sales" replace />} />

        {/* Finance routed children */}
        <Route
          path="/finance/sales"
          element={
            <RoleProtectedRoute roles={["super_admin", "report_viewer", "finance_manager", "store_owner"]}>
              <MainLayout>
                <StoresSales />
              </MainLayout>
            </RoleProtectedRoute>
          }
        />
        <Route
          path="/finance/expenses"
          element={
            <RoleProtectedRoute roles={["super_admin", "finance_manager", "store_owner"]}>
              <MainLayout>
                <Expenses />
              </MainLayout>
            </RoleProtectedRoute>
          }
        />
        <Route
          path="/finance/profit"
          element={
            <RoleProtectedRoute roles={["super_admin", "finance_manager", "store_owner", "report_viewer"]}>
              <MainLayout>
                <ProfitReport />
              </MainLayout>
            </RoleProtectedRoute>
          }
        />
        <Route
          path="/finance/cashflow"
          element={
            <RoleProtectedRoute roles={["super_admin", "finance_manager", "store_owner"]}>
              <MainLayout>
                <CashFlow />
              </MainLayout>
            </RoleProtectedRoute>
          }
        />

        <Route
          path="/finance/payables"
          element={
            <RoleProtectedRoute roles={["super_admin", "finance_manager", "store_owner"]}>
              <MainLayout>
                <Payables />
              </MainLayout>
            </RoleProtectedRoute>
          }
        />

        <Route
          path="/finance/receivables"
          element={
            <RoleProtectedRoute roles={["super_admin", "finance_manager", "store_owner"]}>
              <MainLayout>
                <AntdApp>
                  <Receivables />
                </AntdApp>
              </MainLayout>
            </RoleProtectedRoute>
          }
        />
        <Route
          path="/finance/checks"
          element={
            <RoleProtectedRoute roles={["super_admin", "finance_manager", "store_owner"]}>
              <MainLayout>
                <AntdApp>
                  <CheckManagement />
                </AntdApp>
              </MainLayout>
            </RoleProtectedRoute>
          }
        />
        <Route
          path="/finance/payable-checks"
          element={
            <RoleProtectedRoute roles={["super_admin", "finance_manager", "store_owner"]}>
              <MainLayout>
                <AntdApp>
                  <PayableCheckManagement />
                </AntdApp>
              </MainLayout>
            </RoleProtectedRoute>
          }
        />
        <Route
          path="/finance/prepayments"
          element={
            <RoleProtectedRoute roles={["super_admin", "finance_manager", "store_owner"]}>
              <MainLayout>
                <AntdApp>
                  <CustomerPrepayments />
                </AntdApp>
              </MainLayout>
            </RoleProtectedRoute>
          }
        />

        <Route
          path="/finance/customer-statements"
          element={
            <RoleProtectedRoute roles={["super_admin", "finance_manager", "store_owner"]}>
              <MainLayout>
                <AntdApp>
                  <CustomerStatements />
                </AntdApp>
              </MainLayout>
            </RoleProtectedRoute>
          }
        />

        <Route
          path="/finance/cashier-settlement"
          element={
            <RoleProtectedRoute roles={["super_admin", "admin", "store_owner", "finance_manager", "cashier"]}>
              <MainLayout>
                <CashierSettlement />
              </MainLayout>
            </RoleProtectedRoute>
          }
        />

        <Route
          path="/finance/stock-diff"
          element={
            <RoleProtectedRoute roles={["super_admin", "admin", "store_owner", "finance_manager", "inventory_clerk"]}>
              <MainLayout>
                <AntdApp>
                  <StockDifference />
                </AntdApp>
              </MainLayout>
            </RoleProtectedRoute>
          }
        />

        {/* Product routes */}
        <Route
          path="/products"
          element={
            <RoleProtectedRoute roles={["super_admin", "finance_manager", "inventory_clerk", "store_owner"]}>
              <MainLayout>
                <AntdApp>
                  <Products />
                </AntdApp>
              </MainLayout>
            </RoleProtectedRoute>
          }
        />

        {/* Product Categories routes */}
        <Route
          path="/product-categories"
          element={
            <RoleProtectedRoute roles={["super_admin", "finance_manager", "inventory_clerk", "store_owner"]}>
              <MainLayout>
                <AntdApp>
                  <ProductCategories />
                </AntdApp>
              </MainLayout>
            </RoleProtectedRoute>
          }
        />

        <Route
          path="/tax-categories"
          element={
            <RoleProtectedRoute roles={["super_admin", "finance_manager", "inventory_clerk", "store_owner"]}>
              <MainLayout>
                <AntdApp>
                  <TaxCategories />
                </AntdApp>
              </MainLayout>
            </RoleProtectedRoute>
          }
        />

        {/* Supplier routes */}
        <Route
          path="/suppliers"
          element={
            <RoleProtectedRoute roles={["super_admin", "finance_manager", "inventory_clerk", "store_owner"]}>
              <MainLayout>
                <Suppliers />
              </MainLayout>
            </RoleProtectedRoute>
          }
        />

        {/* Stock routes */}
        <Route
          path="/stock/overview"
          element={
            <RoleProtectedRoute roles={["super_admin", "finance_manager", "inventory_clerk", "store_owner"]}>
              <MainLayout>
                <StockOverview />
              </MainLayout>
            </RoleProtectedRoute>
          }
        />

        <Route
          path="/stock/low-stock"
          element={
            <RoleProtectedRoute roles={["super_admin", "finance_manager", "inventory_clerk", "store_owner"]}>
              <MainLayout>
                <LowStock />
              </MainLayout>
            </RoleProtectedRoute>
          }
        />

        <Route
          path="/stock/adjust"
          element={
            <RoleProtectedRoute roles={["super_admin", "finance_manager", "inventory_clerk", "store_owner"]}>
              <MainLayout>
                <AdjustStock />
              </MainLayout>
            </RoleProtectedRoute>
          }
        />

        <Route
          path="/stock/new-stock"
          element={
            <RoleProtectedRoute roles={["super_admin", "finance_manager", "inventory_clerk", "store_owner"]}>
              <MainLayout>
                <AntdApp>
                  <NewStock />
                </AntdApp>
              </MainLayout>
            </RoleProtectedRoute>
          }
        />

        {/* 仓库盘点路由 */}
        <Route
          path="/stock/stocktaking"
          element={
            <RoleProtectedRoute roles={["super_admin", "finance_manager", "inventory_clerk", "store_owner"]}>
              <MainLayout>
                <AntdApp>
                  <Stocktaking />
                </AntdApp>
              </MainLayout>
            </RoleProtectedRoute>
          }
        />

        {/* PDA盘点路由 */}
        <Route
          path="/pda/stocktaking"
          element={
            <RoleProtectedRoute roles={["super_admin", "inventory_clerk", "store_owner", "cashier"]}>
              <PDAStocktaking />
            </RoleProtectedRoute>
          }
        />

        <Route
          path="/pda/stocktaking/task/:taskId"
          element={
            <RoleProtectedRoute roles={["super_admin", "inventory_clerk", "store_owner", "cashier"]}>
              <PDAStocktaking />
            </RoleProtectedRoute>
          }
        />

        <Route
          path="/pda/stocktaking/task/:taskId/detail"
          element={
            <RoleProtectedRoute roles={["super_admin", "inventory_clerk", "store_owner", "cashier"]}>
              <PDAStocktaking />
            </RoleProtectedRoute>
          }
        />

        {/* Procurement routes — 采购管理（新） */}
        <Route
          path="/procurement/orders"
          element={
            <RoleProtectedRoute roles={["super_admin", "finance_manager", "inventory_clerk", "store_owner"]}>
              <MainLayout>
                <CgOrdersPage />
              </MainLayout>
            </RoleProtectedRoute>
          }
        />
        <Route
          path="/procurement/orders/create"
          element={
            <RoleProtectedRoute roles={["super_admin", "inventory_clerk", "store_owner"]}>
              <MainLayout>
                <CgOrdersCreatePage />
              </MainLayout>
            </RoleProtectedRoute>
          }
        />

        {/* Purchase routes */}
        <Route
          path="/purchase/orders"
          element={
            <RoleProtectedRoute roles={["super_admin", "finance_manager", "inventory_clerk", "store_owner"]}>
              <MainLayout>
                <OrderList />
              </MainLayout>
            </RoleProtectedRoute>
          }
        />

        <Route
          path="/purchase/create"
          element={
            <RoleProtectedRoute roles={["super_admin", "inventory_clerk", "store_owner"]}>
              <MainLayout>
                <CreateOrder />
              </MainLayout>
            </RoleProtectedRoute>
          }
        />

        <Route
          path="/purchase/import-qr"
          element={
            <Navigate to="/purchase/create" replace />
          }
        />

        <Route
          path="/purchase/orders/:id"
          element={
            <RoleProtectedRoute roles={["super_admin", "finance_manager", "inventory_clerk", "store_owner"]}>
              <MainLayout>
                <OrderDetail />
              </MainLayout>
            </RoleProtectedRoute>
          }
        />

        {/* 调拨管理路由 */}
        <Route
          path="/stock/transfer"
          element={
            <RoleProtectedRoute roles={["super_admin", "finance_manager", "inventory_clerk", "store_owner"]}>
              <MainLayout><TransferList /></MainLayout>
            </RoleProtectedRoute>
          }
        />
        <Route
          path="/stock/transfer/create"
          element={
            <RoleProtectedRoute roles={["super_admin", "inventory_clerk", "store_owner"]}>
              <MainLayout><TransferCreate /></MainLayout>
            </RoleProtectedRoute>
          }
        />
        <Route
          path="/stock/transfer/:id"
          element={
            <RoleProtectedRoute roles={["super_admin", "finance_manager", "inventory_clerk", "store_owner"]}>
              <MainLayout><TransferDetail /></MainLayout>
            </RoleProtectedRoute>
          }
        />
        <Route
          path="/stock/transfer/:id/scan"
          element={
            <RoleProtectedRoute roles={["super_admin", "inventory_clerk", "store_owner"]}>
              <MainLayout><TransferScan /></MainLayout>
            </RoleProtectedRoute>
          }
        />

        {/* Remote Clients route */}
        <Route
          path="/remote-clients"
          element={
            <RoleProtectedRoute roles={["super_admin", "inventory_clerk", "store_owner"]}>
              <MainLayout>
                <RemoteClients />
              </MainLayout>
            </RoleProtectedRoute>
          }
        />
        <Route
          path="/yg-orders"
          element={
            <RoleProtectedRoute roles={["super_admin", "inventory_clerk", "store_owner"]}>
              <MainLayout>
                <YGOrders />
              </MainLayout>
            </RoleProtectedRoute>
          }
        />
        <Route
          path="/yg-vendedor"
          element={
            <RoleProtectedRoute roles={["super_admin", "inventory_clerk", "store_owner"]}>
              <MainLayout>
                <YGVendedor />
              </MainLayout>
            </RoleProtectedRoute>
          }
        />
        <Route
          path="/terminals"
          element={
            <RoleProtectedRoute roles={["super_admin", "inventory_clerk", "store_owner"]}>
              <MainLayout>
                <TerminalsManagement />
              </MainLayout>
            </RoleProtectedRoute>
          }
        />

        {/* 用户管理路由 - 新增 */}
        <Route
          path="/users"
          element={
            <RoleProtectedRoute roles={["super_admin", "inventory_clerk", "store_owner"]}>
              <MainLayout>
                <AntdApp>
                  <Users />
                </AntdApp>
              </MainLayout>
            </RoleProtectedRoute>
          }
        />

        {/* Settings route */}
        <Route
          path="/settings"
          element={
            <RoleProtectedRoute roles={["super_admin", "store_owner", "finance_manager", "inventory_clerk", "report_viewer", "cashier", "admin"]}>
              <MainLayout>
                <Settings />
              </MainLayout>
            </RoleProtectedRoute>
          }
        />
        <Route
          path="/receipt-template-editor"
          element={
            <RoleProtectedRoute roles={["super_admin", "store_owner"]}>
              <MainLayout>
                <ReceiptTemplateEditor />
              </MainLayout>
            </RoleProtectedRoute>
          }
        />

        {/* 重定向旧的receipt-templates路径到新的receipt-template-editor路径 */}
        <Route
          path="/receipt-templates"
          element={<Navigate to="/receipt-template-editor" replace />}
        />

        {/* 收银页面路由 - 使用CurrencyProvider包装 */}
        <Route path="/stores/:storeId/cashier" element={
          <RoleProtectedRoute roles={["cashier", "super_admin", "admin", "store_owner"]}>
            <AntdApp>
              <CashierPageWithCurrency />
            </AntdApp>
          </RoleProtectedRoute>
        } />

        {/* 收银页面路由 - 模板2（简洁版） */}
        <Route path="/stores/:storeId/cashier2" element={
          <RoleProtectedRoute roles={["cashier", "super_admin", "admin", "store_owner"]}>
            <AntdApp>
              <CashierPage2WithCurrency />
            </AntdApp>
          </RoleProtectedRoute>
        } />

        {/* 收银页面默认路由 - 无需店铺ID */}
        <Route path="/cashier" element={
          <RoleProtectedRoute roles={["cashier", "super_admin", "admin", "store_owner"]}>
            <AntdApp>
              <CashierPage />
            </AntdApp>
          </RoleProtectedRoute>
        } />

        {/* 收银页面默认路由 - 简洁版，无需店铺ID */}
        <Route path="/cashier2" element={
          <RoleProtectedRoute roles={["cashier", "super_admin", "admin", "store_owner"]}>
            <AntdApp>
              <CashierPage2 />
            </AntdApp>
          </RoleProtectedRoute>
        } />

        {/* Audit Logs route */}
        <Route
          path="/audit-logs"
          element={
            <RoleProtectedRoute roles={["super_admin"]}>
              <MainLayout>
                <AuditLogs />
              </MainLayout>
            </RoleProtectedRoute>
          }
        />

        {/* Customer routes */}
        <Route
          path="/customers"
          element={
            <RoleProtectedRoute roles={["super_admin", "store_owner", "cashier", "admin"]}>
              <MainLayout>
                <Customers />
              </MainLayout>
            </RoleProtectedRoute>
          }
        />

        {/* Member Management routes */}
        <Route
          path="/members"
          element={
            <RoleProtectedRoute roles={["super_admin", "store_owner", "admin"]}>
              <MainLayout>
                <AntdApp>
                  <MembersNew />
                </AntdApp>
              </MainLayout>
            </RoleProtectedRoute>
          }
        />

        <Route
          path="/promotions"
          element={
            <RoleProtectedRoute roles={["super_admin", "store_owner", "admin"]}>
              <MainLayout>
                <AntdApp>
                  <Promotions />
                </AntdApp>
              </MainLayout>
            </RoleProtectedRoute>
          }
        />

        <Route
          path="/promotions/create"
          element={
            <RoleProtectedRoute roles={["super_admin", "store_owner", "admin"]}>
              <MainLayout>
                <AntdApp>
                  <PromotionCreatePage />
                </AntdApp>
              </MainLayout>
            </RoleProtectedRoute>
          }
        />

        <Route
          path="/promotions/edit/:id"
          element={
            <RoleProtectedRoute roles={["super_admin", "store_owner", "admin"]}>
              <MainLayout>
                <AntdApp>
                  <PromotionCreatePage />
                </AntdApp>
              </MainLayout>
            </RoleProtectedRoute>
          }
        />

        <Route
          path="/coupons"
          element={
            <RoleProtectedRoute roles={["super_admin", "store_owner", "admin"]}>
              <MainLayout>
                <AntdApp>
                  <Coupons />
                </AntdApp>
              </MainLayout>
            </RoleProtectedRoute>
          }
        />

        <Route
          path="/points-settings"
          element={
            <RoleProtectedRoute roles={["super_admin", "store_owner"]}>
              <MainLayout>
                <AntdApp>
                  <PointsSettings />
                </AntdApp>
              </MainLayout>
            </RoleProtectedRoute>
          }
        />

        {/* Printer Share route - 打印机共享管理 */}
        <Route
          path="/printer-share"
          element={
            <RoleProtectedRoute roles={["super_admin", "store_owner", "admin"]}>
              <MainLayout>
                <PrinterShare />
              </MainLayout>
            </RoleProtectedRoute>
          }
        />

        {/* 配送商管理路由 */}
        <Route
          path="/transport/list"
          element={
            <RoleProtectedRoute roles={["super_admin", "store_owner", "admin"]}>
              <MainLayout><TransportList /></MainLayout>
            </RoleProtectedRoute>
          }
        />

        <Route
          path="/transport/mode"
          element={
            <RoleProtectedRoute roles={["super_admin", "store_owner", "admin"]}>
              <MainLayout><TransportModeList /></MainLayout>
            </RoleProtectedRoute>
          }
        />

        <Route
          path="/backup"
          element={
            <RoleProtectedRoute roles={["super_admin", "store_owner"]}>
              <MainLayout>
                <BackupPage />
              </MainLayout>
            </RoleProtectedRoute>
          }
        />

        {/* WhatsApp Management route - WhatsApp管理 */}
        <Route
          path="/whatsapp"
          element={
            <RoleProtectedRoute roles={["super_admin", "store_owner", "admin"]}>
              <MainLayout>
                <AntdApp>
                  <WhatsAppManagement />
                </AntdApp>
              </MainLayout>
            </RoleProtectedRoute>
          }
        />

        {/* Redirect all other routes to dashboard */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </Suspense>
    </Router>
  );
};

// Wrapper component for ConfigProvider with i18n
const AppWithI18n: React.FC = () => {
  const { i18n } = useTranslation();
  const [locale, setLocale] = useState(getAntdLocale(i18n.language));

  useEffect(() => {
    const handleLanguageChange = () => {
      setLocale(getAntdLocale(i18n.language));
    };

    i18n.on('languageChanged', handleLanguageChange);
    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [i18n]);

  return (
    <ConfigProvider
      locale={locale}
      theme={{
        token: {
          colorPrimary: '#1890ff',
          borderRadius: 4,
        },
      }}
    >
      <AntdApp>
        <CurrencyProvider>
          <AppContent />
        </CurrencyProvider>
      </AntdApp>
    </ConfigProvider>
  );
};

// Main App component with Redux and Ant Design
const App: React.FC = () => {
  return (
    <Provider store={store}>
      <AppWithI18n />
    </Provider>
  );
};

export default App;
